import { currency } from "@/utils/formats";
import { addVatPence } from "@/utils/payment-plan";

/** When true (default), onsite prices are shown excluding VAT. Set NEXT_PUBLIC_VAT_ENABLED=false for inc-VAT display. */
export function isOnsitePriceExVatDisplay(): boolean {
  return process.env.NEXT_PUBLIC_VAT_ENABLED !== "false";
}

export function getOnsiteDisplayPricePence(
  exVatPence: number | null | undefined,
): number | null {
  if (exVatPence == null) {
    return null;
  }

  return isOnsitePriceExVatDisplay() ? exVatPence : addVatPence(exVatPence);
}

export function formatOnsitePrice(
  exVatPence: number | null | undefined,
): string | null {
  const displayPence = getOnsiteDisplayPricePence(exVatPence);
  if (displayPence == null) {
    return null;
  }

  return currency(displayPence);
}

export function parseExVatPenceFromGbpString(value: string): number | null {
  const normalized = value.replace(/[£,\s]/g, "");
  const pounds = Number.parseFloat(normalized);
  if (!Number.isFinite(pounds)) {
    return null;
  }

  return Math.round(pounds * 100);
}

export function formatOnsitePriceFromGbpString(exVatLabel: string): string {
  const pence = parseExVatPenceFromGbpString(exVatLabel);
  if (pence == null) {
    return exVatLabel;
  }

  return formatOnsitePrice(pence) ?? exVatLabel;
}

export function formatOnsitePriceFromPounds(exVatPounds: number): string | null {
  return formatOnsitePrice(Math.round(exVatPounds * 100));
}

export function getOnsiteVatLabel(): string | null {
  return isOnsitePriceExVatDisplay() ? "excl. VAT" : "inc. VAT";
}

type OnsiteEventPriceDetail = {
  wasPrice?: number | null;
  standardPrice?: number | null;
  package?: boolean | null;
};

export function resolveOnsiteEventPriceComparison(
  eventDetail: OnsiteEventPriceDetail | null | undefined,
  seatPricePence: number | null | undefined,
) {
  const seatExVat = Math.round(Number(seatPricePence || 0));
  const wasPrice = Math.round(Number(eventDetail?.wasPrice || 0));
  const standardPrice = Math.round(Number(eventDetail?.standardPrice || 0));

  const hasSeatDiscount =
    seatExVat > 0 && standardPrice > 0 && seatExVat < standardPrice;

  const comparePrice =
    eventDetail?.package && wasPrice > 0
      ? wasPrice
      : hasSeatDiscount
        ? standardPrice
        : 0;

  const showWasPrice =
    Boolean(eventDetail?.package && wasPrice > 0) || hasSeatDiscount;

  return {
    currentPrice: seatExVat,
    comparePrice,
    showWasPrice,
  };
}

/** Checkout headline total: ex-VAT subtotal when prices display ex-VAT, full inc-VAT total otherwise. */
export function getOnsiteCheckoutSummaryTotalPence(
  exVatSubtotalPence: number,
  fullTotalPence: number,
): number {
  return isOnsitePriceExVatDisplay()
    ? exVatSubtotalPence
    : fullTotalPence;
}

type OnsiteEventPricing = {
  wasPrice?: number | null;
  standardPrice?: number | null;
  events?: Array<{
    registrations?: unknown[];
    seats?: number;
    seat_price?: number;
    price?: number;
  }>;
};

/** Resolve ex-VAT listing price from event detail (next available seat or standard). */
export function resolveOnsiteListingExVatPricePence(
  eventData?: OnsiteEventPricing | null,
): number | null {
  if (!eventData) {
    return null;
  }

  const nextEvent = eventData.events?.find(
    (instance) => (instance.registrations?.length ?? 0) < (instance.seats ?? 0),
  );

  const eventPrice = nextEvent?.seat_price ?? nextEvent?.price ?? null;
  const standardPrice = eventData.standardPrice ?? null;

  if (
    eventPrice != null &&
    standardPrice != null &&
    eventPrice < standardPrice
  ) {
    return eventPrice;
  }

  if (eventPrice != null) {
    return eventPrice;
  }

  return standardPrice;
}

/** Display-ready pence for landing/listing cards from event detail. */
export function resolveOnsiteListingDisplayPricePence(
  eventData?: OnsiteEventPricing | null,
): number | null {
  return getOnsiteDisplayPricePence(resolveOnsiteListingExVatPricePence(eventData));
}
